import { css } from '@emotion/css';
import { captureException } from '@sentry/react';
import { useEffect } from 'react';
import { useRouteError } from 'react-router';

import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { Colors } from 'dg-web-shared/ui/vars';

/**
 * Last resort recovery screen for anything that reaches the root route.
 *
 * Without an `errorElement` react-router renders its own DefaultErrorComponent,
 * which replaces the whole app with "Unexpected Application Error!" plus a raw
 * <pre> stack dump. On a payment app that is a total session loss with no way
 * out other than the user knowing to reload by hand (Sentry CUSTOMER-PROD-DWM).
 *
 * Recovery is a hard `window.location.reload()`, deliberately NOT a
 * `navigate('/')` like the precedent in
 * java/onPremise/trxLine/frontend/src/index.tsx. The crash we see in production
 * is Chrome-for-iOS page translation rewriting text nodes into <font> elements
 * underneath React, which leaves React's stored text node references stale and
 * makes the next removeChild/insertBefore throw. A client side navigation would
 * re-render into that very same mutated DOM and throw again; only a full
 * document reload gives React a tree it owns.
 */
export function RootErrorPage() {
    const error = useRouteError();
    const isReloading = takeAutomaticReloadSlot();

    useEffect(() => {
        captureException(error, {
            tags: { rootErrorRecovery: isReloading ? 'reload' : 'manual' },
        });
        if (isReloading) {
            window.location.reload();
        }
    }, [error, isReloading]);

    if (isReloading) {
        // The reload is already on its way - showing a message here would only
        // flash for a few frames.
        return <div className={backgroundStyle} />;
    }

    // Intentionally static: no local state, no layout components, no store or
    // router reads beyond the error itself. Once this has rendered React never
    // touches these nodes again, so a translation engine mutating them cannot
    // make the recovery screen itself crash the way the app just did.
    return (
        <div className={backgroundStyle}>
            <div className={cardStyle}>
                <h1 className={titleStyle}>
                    <Localized
                        de="Es ist ein unerwarteter Fehler aufgetreten"
                        fr="Une erreur inattendue s'est produite"
                        it="Si è verificato un errore imprevisto"
                        en="An unexpected error occurred"
                    />
                </h1>
                <p>
                    <Localized
                        de="Bitte laden Sie die App neu. Ihre laufenden Parkvorgänge sind davon nicht betroffen."
                        fr="Veuillez recharger l'application. Vos stationnements en cours ne sont pas affectés."
                        it="Voglia ricaricare l'applicazione. I suoi parcheggi in corso non sono interessati."
                        en="Please reload the app. Your running parking transactions are not affected."
                    />
                </p>
                <button
                    className={buttonStyle}
                    onClick={() => {
                        window.location.href = '/';
                    }}
                    type="button"
                >
                    <Localized
                        de="Neu laden"
                        fr="Recharger"
                        it="Ricarica"
                        en="Reload"
                    />
                </button>
            </div>
        </div>
    );
}

const AUTOMATIC_RELOAD_KEY = 'parkingpay.rootErrorAutomaticReloadAt';

/**
 * A second crash within this window means the reload did not help, so we stop
 * reloading and let the user decide instead. A crash after it means the app was
 * usable in between and has earned a fresh attempt.
 */
const AUTOMATIC_RELOAD_COOLDOWN_MS = 60 * 1000;

/**
 * Decided once per document, because StrictMode double invokes render bodies
 * and a re-render must not consume a second slot.
 */
let automaticReloadSlot: boolean | null = null;

function takeAutomaticReloadSlot(): boolean {
    if (automaticReloadSlot === null) {
        automaticReloadSlot = readAndTakeAutomaticReloadSlot();
    }
    return automaticReloadSlot;
}

/**
 * Guards the reload with a single retry per incident, kept in sessionStorage so
 * it survives exactly the reload it is guarding. The app already force reloads
 * on version mismatch in dg-web-shared/common/utils/UpdateUtils.ts, and an
 * unguarded reload-on-error compounds with that into a reload loop.
 */
function readAndTakeAutomaticReloadSlot(): boolean {
    try {
        const lastReloadAt = Number(
            window.sessionStorage.getItem(AUTOMATIC_RELOAD_KEY),
        );
        if (
            lastReloadAt > 0 &&
            Date.now() - lastReloadAt < AUTOMATIC_RELOAD_COOLDOWN_MS
        ) {
            return false;
        }
        window.sessionStorage.setItem(AUTOMATIC_RELOAD_KEY, String(Date.now()));
        return true;
    } catch {
        // sessionStorage can throw (Safari private mode, storage disabled by
        // policy). Without it we cannot rule out a loop, so we don't reload.
        return false;
    }
}

const backgroundStyle = css({
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: Colors.white,
    color: Colors.typo1,
    ...Typo.body,
});

const cardStyle = css({
    maxWidth: '480px',
});

const titleStyle = css({
    ...Typo.heading2,
    color: Colors.blue,
    margin: '0 0 16px',
});

const buttonStyle = css({
    ...Typo.robotoMedium,
    display: 'block',
    marginTop: '24px',
    padding: '0 24px',
    height: '40px',
    border: 0,
    borderRadius: '2px',
    background: Colors.blue,
    color: Colors.white,
    fontSize: '16px',
    cursor: 'pointer',
});
